/**
 * The panel frame (RFC 0266 §3c) — panels are inset cards on a padded ground,
 * not columns divided by hairlines.
 *
 * Legacy's rule, ported exactly (FileCanvasPanel.tsx:3840-3844, :4028-4033):
 * the ground carries the padding, the BODY carries the rounded border, and the
 * header sits OUTSIDE the frame so the border wraps content only. The frame
 * drops in full-screen and when the panel draws its own sub-frames.
 *
 * One component so the three panels stop hand-rolling their chrome — which is
 * why their headers used to disagree by a pixel (h-9 vs h-10).
 */

import type { ReactNode } from 'react'
import { usePaneDragHandle } from './ContentPanes'

export const PANEL_HEADER_H = 'h-9'

export function Panel({
  header,
  focused = false,
  framed = true,
  padded = true,
  floating = false,
  children,
}: {
  /** Rendered in the header row, outside the frame. */
  header?: ReactNode
  /** Lifts the border colour — the single focus cue (§3b). */
  focused?: boolean
  /** False for full-screen or when the body draws its own frames. */
  framed?: boolean
  /** False for panels that read as raw surface (sidebar, agent), not cards. */
  padded?: boolean
  /** The hover-reveal overlay: reads as an overlay, not as structure. */
  floating?: boolean
  children: ReactNode
}) {
  // The header doubles as the layout drag handle when this panel sits in a layout
  // tree (PanelTree provides it; null anywhere else). A drag must start on the
  // header's own surface, so a pointerdown that lands on one of its controls is
  // left alone — otherwise every header button would also be a drag.
  const dragHandle = usePaneDragHandle()
  const onHeaderPointerDown = dragHandle
    ? (e: React.PointerEvent) => {
        if (e.button !== 0) return
        const el = e.target as HTMLElement
        if (el.closest('button, a, input, [role="button"]')) return
        e.preventDefault()
        dragHandle.begin(e)
      }
    : undefined

  return (
    <div
      // w-full and min-w-0: a panel FILLS the pane it was put in. Without the
      // width it is a flex item with no basis, so it shrink-to-fits its content
      // — which is why the session list drew a 220px column inside a 1000px
      // pane, while the canvas (whose own wrapper supplies flex-1) looked fine.
      // No top padding under a header: the header row is the top edge, so a
      // document's header sits on the same line as a session's unpadded one.
      className={`flex h-full min-h-0 w-full min-w-0 flex-col ${
        padded ? (header !== undefined ? 'px-1.5 pb-1.5' : 'p-1.5') : ''
      } ${
        floating
          ? 'overflow-hidden rounded-xl border border-border bg-background shadow-2xl'
          : ''
      }`}
    >
      {header !== undefined && (
        <div
          onPointerDown={onHeaderPointerDown}
          className={`flex ${PANEL_HEADER_H} shrink-0 items-center gap-1 ${
            padded ? 'px-1.5' : 'px-2'
          } ${dragHandle ? 'cursor-grab active:cursor-grabbing' : ''}`}
        >
          {header}
        </div>
      )}
      <div
        className={`min-h-0 flex-1 overflow-hidden ${
          framed
            ? `rounded-lg border ${
                focused
                  ? 'border-[color:var(--ws-border-focus)]'
                  : 'border-border'
              }`
            : ''
        }`}
      >
        {children}
      </div>
    </div>
  )
}

/** The panel-name treatment: a micro-label, as the existing panels already do. */
export function PanelLabel({ children }: { children: ReactNode }) {
  return (
    <span className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
      {children}
    </span>
  )
}

/** The icon-button treatment shared by every panel header control. */
export function PanelAction({
  title,
  onClick,
  active = false,
  children,
}: {
  title: string
  onClick?: () => void
  active?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`rounded p-0.5 hover:bg-muted hover:text-foreground ${
        active ? 'text-foreground' : 'text-muted-foreground'
      }`}
    >
      {children}
    </button>
  )
}
